if (!customElements.get('drbo-partner-notice')) {
  customElements.define('drbo-partner-notice', class extends HTMLElement {
    connectedCallback() {
      this.overlay = this.querySelector('.drbo-partner-overlay');
      if (!this.overlay) return;
      this.dialog = this.overlay.querySelector('[role="dialog"]');
      this.events = new AbortController();
      const options = { signal: this.events.signal };
      document.addEventListener('click', event => {
        const trigger = event.target.closest('[data-partner-open]');
        if (trigger?.dataset.partnerOpen === this.dataset.noticeId) this.open(trigger);
      }, options);
      this.overlay.addEventListener('click', event => {
        if (event.target === this.overlay || event.target.closest('[data-partner-close]')) this.close();
      }, options);
      document.addEventListener('keydown', event => this.keydown(event), options);
      document.addEventListener('focusin', event => {
        if (this.opened) {
          if (!this.overlay.contains(event.target) && !this.cookie?.contains(event.target)) this.dialog.focus();
          return;
        }
        if (event.target.dataset.partnerOpen === this.dataset.noticeId) {
          this.pauseSlider(event.target);
        }
      }, options);
      document.addEventListener('focusout', event => {
        if (!this.opened && event.target.dataset.partnerOpen === this.dataset.noticeId) this.releaseSlider();
      }, options);
    }

    disconnectedCallback() {
      this.close(false);
      this.releaseSlider();
      this.events?.abort();
    }

    pauseSlider(trigger) {
      if (this.pauseState) return;
      const slider = trigger.closest('.swiper')?.swiper;
      if (!slider?.autoplay || slider.destroyed) return;
      const options = slider.params.autoplay;
      this.pauseState = {
        slider,
        resume: slider.el.dataset.autoplay === 'true' && options?.enabled !== false && slider.autoplay.running,
        disableOnInteraction: options?.disableOnInteraction
      };
      if (options) options.disableOnInteraction = true;
      slider.autoplay.stop();
    }

    releaseSlider() {
      if (!this.pauseState) return;
      const { slider, resume, disableOnInteraction } = this.pauseState;
      this.pauseState = null;
      if (slider.destroyed) return;
      if (slider.params.autoplay) slider.params.autoplay.disableOnInteraction = disableOnInteraction;
      if (resume) slider.autoplay.start();
    }

    syncCookie() {
      this.cookie = Array.from(document.querySelectorAll('.cookie-bar')).find(element => {
        return !element.classList.contains('is-hidden') && getComputedStyle(element).display !== 'none' && getComputedStyle(element).visibility !== 'hidden';
      });
      this.overlay.style.setProperty('--partner-cookie-height', this.cookie ? `${this.cookie.offsetHeight}px` : '0px');
      this.dialog.setAttribute('aria-modal', this.cookie ? 'false' : 'true');
    }

    isolateBackground() {
      for (const element of document.body.children) {
        if (element === this.overlay || element.matches('.cookie-bar, script, style, link') || element.querySelector('.cookie-bar') || this.inertStates.has(element)) continue;
        this.inertStates.set(element, element.inert);
        element.inert = true;
      }
    }

    open(trigger) {
      if (this.opened) return;
      this.opened = true;
      this.trigger = trigger;
      this.pauseSlider(trigger);
      this.syncCookie();
      document.body.append(this.overlay);
      this.overlay.hidden = false;
      this.overflow = document.documentElement.style.overflow;
      document.documentElement.style.overflow = 'hidden';
      this.inertStates = new Map();
      this.isolateBackground();
      this.backgroundObserver = new MutationObserver(() => this.isolateBackground());
      this.backgroundObserver.observe(document.body, { childList: true });
      trigger.setAttribute('aria-expanded', 'true');
      this.cookieObserver = new MutationObserver(() => this.syncCookie());
      document.querySelectorAll('.cookie-bar').forEach(element => {
        this.cookieObserver.observe(element, { attributes: true, attributeFilter: ['class', 'style'] });
      });
      this.resizeObserver = new ResizeObserver(() => this.syncCookie());
      document.querySelectorAll('.cookie-bar').forEach(element => this.resizeObserver.observe(element));
      this.dialog.querySelector('[data-partner-close]').focus({ preventScroll: true });
    }

    close(restoreFocus = true) {
      if (!this.opened) return;
      this.opened = false;
      this.cookieObserver?.disconnect();
      this.resizeObserver?.disconnect();
      this.backgroundObserver?.disconnect();
      this.overlay.hidden = true;
      this.append(this.overlay);
      document.documentElement.style.overflow = this.overflow;
      this.inertStates.forEach((inert, element) => { element.inert = inert; });
      this.trigger?.setAttribute('aria-expanded', 'false');
      if (restoreFocus && this.trigger?.isConnected) this.trigger.focus({ preventScroll: true });
      if (!restoreFocus || document.activeElement !== this.trigger) this.releaseSlider();
    }

    keydown(event) {
      if (!this.opened) return;
      if (event.key === 'Escape') {
        if (this.cookie?.contains(event.target)) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        this.close();
      }
      if (event.key !== 'Tab') return;
      const selector = 'button:not([disabled]), a[href], [tabindex="0"]';
      const focusable = [...this.overlay.querySelectorAll(selector), ...this.cookie?.querySelectorAll(selector) || []].filter(element => element.getClientRects().length && getComputedStyle(element).visibility !== 'hidden');
      const current = focusable.indexOf(document.activeElement);
      if (!focusable.length) { event.preventDefault(); this.dialog.focus(); return; }
      event.preventDefault();
      const next = current < 0 ? (event.shiftKey ? focusable.length - 1 : 0) : (current + (event.shiftKey ? -1 : 1) + focusable.length) % focusable.length;
      focusable[next].focus();
    }
  });
}
